import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { SamplesOptions, WidgetLayout, WidgetType } from '../shared/types';
import { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '@texas/components/shared/Icons';
import { TableView } from './TableView';
import { MinifiedView } from './MinifiedView';
import { useSamplesWidget } from './useSamplesWidget';

interface Props extends BaseWidgetProps {
  options?: SamplesOptions;
}

export const SamplesWidgetContext = createContext<ReturnType<
  typeof useSamplesWidget
> | null>(null);

export function SamplesWidget({
  color,
  customTitle,
  options,
  widgetId,
}: Props) {
  const { t } = useTranslation();

  const samplesWidget = useSamplesWidget({ options, widgetId });

  return (
    <SamplesWidgetContext.Provider value={samplesWidget}>
      <WidgetWrapper
        widgetId={widgetId}
        loading={samplesWidget.loading}
        settings={options?.componentSettings}
        customColor={color}
        type={WidgetType.TrackSamples}
        style="default"
        header={customTitle ?? t('sample.beingPrepared')}
        extendedMenu={samplesWidget.useMultiSelect.menuItems}
        toolbox={
          <WidgetToolbox
            layout={samplesWidget.layout}
            handleLayoutChange={(view: WidgetLayout) => {
              samplesWidget.setLayout(view);
              samplesWidget.useMultiSelect.setSelectedRows({});
            }}
          />
        }
      >
        <>
          {samplesWidget.layout === 'table' && <TableView />}
          {samplesWidget.layout === 'minified' && <MinifiedView />}
          {samplesWidget.useMultiSelect.modalComponents}
        </>
      </WidgetWrapper>
    </SamplesWidgetContext.Provider>
  );
}

function WidgetToolbox({
  layout,
  handleLayoutChange,
}: {
  layout: WidgetLayout;
  handleLayoutChange: (view: WidgetLayout) => void;
}) {
  const { t } = useTranslation();
  return (
    <Flex gap={2}>
      <Tooltip label={t('view.minified')}>
        <IconButton
          variant={layout === 'minified' ? 'texas-solid' : 'texas-light'}
          size="sm"
          icon={<Icons.ViewCard />}
          aria-label={t('view.minified')}
          onClick={() => handleLayoutChange('minified')}
        />
      </Tooltip>
      <Tooltip label={t('view.tableView')}>
        <IconButton
          variant={layout === 'table' ? 'texas-solid' : 'texas-light'}
          size="sm"
          icon={<Icons.ViewTable />}
          aria-label={t('view.tableView')}
          onClick={() => handleLayoutChange('table')}
        />
      </Tooltip>
    </Flex>
  );
}
